import { BaseCitationPlugin } from '@alpod-ai-writer/plate-plugins/citations';
import { BaseFootnotesPlugin } from '@alpod-ai-writer/plate-plugins/footnotes';
import { BaseLinkPlugin } from '@alpod-ai-writer/plate-plugins/link';
import { BaseShortcutPlugin } from '@alpod-ai-writer/plate-plugins/shortcuts';
import { BaseSuggestionPlugin } from '@alpod-ai-writer/plate-plugins/suggestion';
import { DocumentSectionType, type DocumentTemplate } from '@alpod-ai-writer/shared';
import { withYjs, YjsEditor } from '@slate-yjs/core';
import {
    createSlateEditor,
    ElementApi,
    PathApi,
    type SlateEditor,
    type TElement,
    TextApi,
    type TText
} from '@udecode/plate';
import * as Y from 'yjs';

import { basicNodesPlugins } from '../hooks/useTextEditor.hook';

const SUGGESTION_KEY_PREFIX = `${BaseSuggestionPlugin.key}_`;

interface AppSuggestionData {
    id: string;
    type: 'insert' | 'remove' | 'update';
    isLineBreak?: boolean;
    userId?: string;
    createdAt?: number;
}

export async function acceptAllSuggestionsInDocument(
    template: DocumentTemplate,
    yDoc: Y.Doc
): Promise<void> {
    const contentFieldIds = template.config.sections
        .filter((section) => section.type === DocumentSectionType.Content)
        .map((section) => section.content?.id)
        .filter((id): id is string => !!id);

    for (const fieldId of contentFieldIds) {
        const yNode = yDoc.get(fieldId, Y.XmlText);

        if (!(yNode instanceof Y.XmlText)) continue;

        acceptAllSuggestionsInYNode(yNode);

        // Yield so the modal's spinner has a chance to paint between sections —
        // otherwise the entire loop runs in one JS task and the UI freezes.
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
}

function acceptAllSuggestionsInYNode(yNode: Y.XmlText): void {
    const editor = createSlateEditor({
        plugins: [
            ...basicNodesPlugins,
            BaseSuggestionPlugin,
            BaseCitationPlugin,
            BaseFootnotesPlugin,
            BaseShortcutPlugin,
            BaseLinkPlugin,
        ],
    });

    const yjsEditor = withYjs(editor as any, yNode, {
        autoConnect: false,
        localOrigin: Symbol('accept-all-suggestions-headless'),
    });

    YjsEditor.connect(yjsEditor);

    try {
        if (editor.children.length === 0) return;

        acceptAllSuggestionsForApp(editor as any);

        // Also process suggestion marks inside footnote content (stored as JSON attribute)
        const footnoteEntries = [
            ...(editor as any).api.nodes({
                at: [],
                mode: 'all',
                match: (n: unknown) => ElementApi.isElement(n as TElement) && (n as Record<string, unknown>).type === 'footnote',
            }),
        ] as [TElement, unknown[]][];

        for (const [node, path] of footnoteEntries) {
            const fn = node as Record<string, unknown>;
            if (!Array.isArray(fn.footnotes)) continue;
            const cleaned = acceptSuggestionsInJsonNodes(fn.footnotes as unknown[]);
            (editor as any).tf.setNodes({ footnotes: cleaned }, { at: path });
        }

        YjsEditor.flushLocalChanges(yjsEditor);
    } finally {
        YjsEditor.disconnect(yjsEditor);
    }
}

// Custom accept-all that mirrors plate-plugins' acceptAllSuggestions but reads
// suggestion data from the `suggestion_<id>` key (the shape diffToSuggestions /
// getSuggestionProps actually emit). Upstream's transform expects block
// suggestions as `element.suggestion = { id, type, ... }` — but our app stores
// them as `suggestion: true` + a sibling `suggestion_<id>: {...}` key, so the
// upstream `n.suggestion.type === 'insert'` check always misses on blocks.
function acceptAllSuggestionsForApp(editor: SlateEditor): void {
    editor.tf.withoutNormalizing(() => {
        // Step 1: line-break suggestions of type 'remove' merge their block with the next.
        const lineBreakRemoveBlocks = [
            ...editor.api.nodes<TElement>({
                at: [],
                match: (n) => {
                    if (!ElementApi.isElement(n)) return false;
                    const data = getNodeSuggestionData(n);
                    return data?.type === 'remove' && data.isLineBreak === true;
                },
            }),
        ];

        lineBreakRemoveBlocks.reverse().forEach(([, path]) => {
            editor.tf.mergeNodes({ at: PathApi.next(path) });
        });

        // Step 2a: Remove 'remove' blocks first — must be separate from text removal
        // so Slate doesn't try to remove already-gone children after their parent is deleted.
        editor.tf.removeNodes({
            at: [],
            mode: 'all',
            match: (n) => {
                if (!ElementApi.isElement(n)) return false;
                const data = getNodeSuggestionData(n);
                return data?.type === 'remove' && data.isLineBreak !== true;
            },
        });

        // Step 2b: Remove 'remove' text nodes within surviving blocks.
        editor.tf.removeNodes({
            at: [],
            mode: 'all',
            match: (n) => {
                if (!TextApi.isText(n)) return false;
                return getNodeSuggestionData(n)?.type === 'remove';
            },
        });

        // Step 3: surviving 'insert' / 'update' / linebreak-insert suggestions
        //         keep their content but lose their suggestion metadata.
        const survivors = [
            ...editor.api.nodes({
                at: [],
                mode: 'all',
                match: (n) => {
                    if (!TextApi.isText(n) && !ElementApi.isElement(n)) return false;
                    return !!getNodeSuggestionData(n as TElement | TText);
                },
            }),
        ];

        survivors.reverse().forEach(([n, path]) => {
            const keys = collectSuggestionKeys(n);
            if (keys.length === 0) return;
            editor.tf.unsetNodes(keys, { at: path });
        });
    });
}

// Pure JSON version of accept-all — used for footnote content which is stored as a JSON attribute.
function acceptSuggestionsInJsonNodes(nodes: unknown[]): unknown[] {
    // Step 1: remove 'remove' type nodes
    const afterRemove = nodes.filter((node) => {
        if (!node || typeof node !== 'object') return true;
        const data = getNodeSuggestionData(node as TElement | TText);
        return !data || data.type !== 'remove';
    });

    // Step 2: unset suggestion keys from surviving nodes, recurse into children/footnotes
    return afterRemove.map((node) => {
        if (!node || typeof node !== 'object') return node;
        const n = node as Record<string, unknown>;
        const suggestionKeys = collectSuggestionKeys(n);
        const cleaned: Record<string, unknown> = suggestionKeys.length > 0
            ? Object.fromEntries(Object.entries(n).filter(([k]) => !suggestionKeys.includes(k)))
            : { ...n };
        if (Array.isArray(cleaned.children)) {
            cleaned.children = acceptSuggestionsInJsonNodes(cleaned.children as unknown[]);
        }
        return cleaned;
    });
}

function getNodeSuggestionData(node: TElement | TText): AppSuggestionData | undefined {
    const directSuggestion = (node as { suggestion?: unknown }).suggestion;

    // Upstream-style: object directly under `suggestion`.
    if (directSuggestion && typeof directSuggestion === 'object' && 'type' in (directSuggestion as object)) {
        return directSuggestion as AppSuggestionData;
    }

    // App-style: `suggestion: true` (or any truthy non-object) plus a sibling
    // `suggestion_<id>` key holding the data. Several can coexist (multi-user)
    // but we only need one to drive accept logic.
    if (directSuggestion) {
        for (const key of Object.keys(node)) {
            if (key.startsWith(SUGGESTION_KEY_PREFIX)) {
                const value = (node as Record<string, unknown>)[key];
                if (value && typeof value === 'object' && 'type' in (value as object)) {
                    return value as AppSuggestionData;
                }
            }
        }
    }

    return undefined;
}

function collectSuggestionKeys(node: unknown): string[] {
    if (!node || typeof node !== 'object') return [];
    const keys: string[] = [];
    for (const key of Object.keys(node)) {
        if (key === BaseSuggestionPlugin.key || key.startsWith(SUGGESTION_KEY_PREFIX)) {
            keys.push(key);
        }
    }
    return keys;
}
